import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import {
  IntegrationLimitedDto,
  IntegrationLogLevel,
  IntegrationScriptLogDto,
} from '@/lib/integrationTypes/integrationTypes';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { buildFormSchema } from '@/app/IntegrationHub/buildFormSchema';

import DataDrivenField from '@/features/Devices/IntegrationVariables/DataDrivenField';

interface ConnectionFormProps {
  integration: IntegrationLimitedDto;
  onVariableSubmit: SubmitHandler<FieldValues>;
  onFormChange?: (data: FieldValues) => void;
  submitText?: string;
  currentlyProcessing?: boolean;
  logs?: IntegrationScriptLogDto[] | null;
  border?: boolean;
  defaultValues?: FieldValues;
}

export default function ConnectionForm({
  logs,
  integration,
  onVariableSubmit,
  onFormChange,
  submitText,
  currentlyProcessing,
  border,
  defaultValues,
}: ConnectionFormProps) {
  const form = useForm(buildFormSchema(integration, defaultValues));

  form.watch((data) => {
    if (onFormChange) {
      onFormChange(data);
    }
  });

  if (!integration.integrationFields) {
    return (
      <section className="mt-6 rounded-xl border bg-[#111]">
        <div className="p-6">
          <h2 className="pb-6 text-xl font-semibold">Configure {integration.name}</h2>
          <p className="text-gray-400">
            No configuration options available. The integration likely has not been completed, or
            something has gone wrong.
          </p>
        </div>
      </section>
    );
  }

  const fields = [];

  for (const field of integration.integrationFields) {
    fields.push(<DataDrivenField integrationField={field} form={form} key={field.id} />);
  }

  return (
    <section
      className={`mt-6 rounded-xl ${border === undefined || border === true ? 'border' : ''} bg-[#111]`}
    >
      <div className="p-6">
        <h2 className="text-xl font-semibold">Connection Details</h2>
        <p className="text-gray-400">Configure the fields below to connect iHub</p>
        {integration.description ? (
          <h3 className="mt-6 text-lg font-semibold">{integration.description}</h3>
        ) : null}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onVariableSubmit)}
            className="space-y-8"
            autoComplete="off"
          >
            {fields.length == 0 && (
              <div className="my-4 flex flex-row items-center justify-between rounded-lg border p-4">
                <p className="text-gray-400">
                  No configuration options available. You're ready to go!
                </p>

                <Button type="submit" disabled={currentlyProcessing}>
                  {submitText ?? 'Connect'}
                </Button>
              </div>
            )}

            {fields.length > 0 && (
              <>
                {fields}
                <Button type="submit" disabled={currentlyProcessing}>
                  {submitText ?? 'Connect'}
                </Button>
              </>
            )}
          </form>
        </Form>
        {logs ? (
          <div className="mt-6">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button>Logs</Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 flex flex-col gap-2">
                  {logs.map((log, index) => (
                    <div key={index}>
                      <hr className="mb-1 w-full bg-gray-500" />
                      <div className="flex flex-row items-center gap-3">
                        <div
                          className={
                            'rounded-sm px-2 py-1 ' +
                            (log.level === IntegrationLogLevel.Info
                              ? 'bg-blue-800'
                              : log.level === IntegrationLogLevel.Warning
                                ? 'bg-yellow-800'
                                : log.level === IntegrationLogLevel.Error
                                  ? 'bg-red-800'
                                  : 'bg-gray-800')
                          }
                        >
                          {log.level === IntegrationLogLevel.Info
                            ? 'Info'
                            : log.level === IntegrationLogLevel.Warning
                              ? 'Warning'
                              : log.level === IntegrationLogLevel.Error
                                ? 'Error'
                                : 'Debug'}{' '}
                        </div>
                        <p>{log.message}</p>
                      </div>
                      <hr className="mt-1 w-full bg-gray-500" />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : undefined}
      </div>
    </section>
  );
}
